import { useEffect, useState } from 'react'
import { CheckCircle, Loader2, XCircle } from 'lucide-react'

interface ServiceStep {
  id: string
  label: string
  status: 'waiting' | 'loading' | 'done' | 'error'
}

interface Props {
  onReady: () => void
}

export function SplashScreen({ onReady }: Props) {
  const [steps, setSteps] = useState<ServiceStep[]>([
    { id: 'postgres', label: 'PostgreSQL başlatılıyor...', status: 'loading' },
    { id: 'api', label: 'iNova Muhasebe API başlatılıyor...', status: 'waiting' },
    { id: 'ready', label: 'Hazırlanıyor...', status: 'waiting' },
  ])
  const [error, setError] = useState<string | null>(null)

  const updateStep = (id: string, status: ServiceStep['status'], label?: string) => {
    setSteps((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status, ...(label ? { label } : {}) } : s))
    )
  }

  useEffect(() => {
    let cancelled = false

    async function pollServices() {
      if (!window.__TAURI__) {
        // Tauri dışında direkt geç
        onReady()
        return
      }

      const { invoke } = await import('@tauri-apps/api/core')

      // PostgreSQL kontrolü
      let postgresReady = false
      for (let i = 0; i < 40; i++) {
        if (cancelled) return
        try {
          const status = await invoke<{ postgres: boolean; api: boolean; ready: boolean; message: string }>('get_service_status')
          if (status.postgres) {
            postgresReady = true
            updateStep('postgres', 'done', 'PostgreSQL hazır ✓')
            updateStep('api', 'loading')
            break
          }
        } catch {
          // devam
        }
        await sleep(1000)
      }

      if (!postgresReady) {
        updateStep('postgres', 'error', 'PostgreSQL başlatılamadı')
        setError('PostgreSQL başlatılamadı. Loglar: %APPDATA%\\iNova\\logs')
        return
      }

      // API kontrolü
      let apiReady = false
      for (let i = 0; i < 40; i++) {
        if (cancelled) return
        try {
          const status = await invoke<{ postgres: boolean; api: boolean }>('get_service_status')
          if (status.api) {
            apiReady = true
            updateStep('api', 'done', 'API hazır ✓')
            break
          }
        } catch {
          // devam
        }
        await sleep(1000)
      }

      if (!apiReady) {
        updateStep('api', 'error', 'API başlatılamadı')
        setError('API başlatılamadı. Lütfen uygulamayı yeniden başlatın.')
        return
      }

      updateStep('ready', 'done', 'Hazır!')
      await sleep(500)
      if (!cancelled) onReady()
    }

    pollServices()
    return () => { cancelled = true }
  }, [onReady])

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-bg-base z-50">
      <div className="flex flex-col items-center gap-8 max-w-sm w-full px-8">
        {/* Logo */}
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white text-3xl font-bold">N</span>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">iNova Muhasebe</h1>
          <p className="text-sm text-text-secondary mt-1">Muhasebe & ERP Yazılımı</p>
        </div>

        {/* Adımlar */}
        <div className="w-full space-y-3">
          {steps.map((step) => (
            <div key={step.id} className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {step.status === 'done' && <CheckCircle className="w-5 h-5 text-success" />}
                {step.status === 'loading' && <Loader2 className="w-5 h-5 text-primary animate-spin" />}
                {step.status === 'error' && <XCircle className="w-5 h-5 text-error" />}
                {step.status === 'waiting' && <div className="w-5 h-5 rounded-full border-2 border-bg-border" />}
              </div>
              <span className={`text-sm ${step.status === 'error' ? 'text-error' : step.status === 'done' ? 'text-text-primary' : 'text-text-secondary'}`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>

        {/* Hata mesajı */}
        {error && (
          <div className="w-full rounded-lg bg-error/10 border border-error/20 p-4">
            <p className="text-sm text-error">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 text-xs text-primary hover:underline"
            >
              Yeniden dene
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms))
}
